/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { ImageLoader } from 'components/common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CustomCreatableSelect,
  CustomInput,
  CustomTextarea,
  CustomAddressSelect,
  CustomCategorySelect
} from 'components/formik';
import { Field, Form, Formik } from 'formik';
import { useFileHandler } from 'hooks';
import PropType from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { displayActionMessage } from 'helpers/utils';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';

const ProductForm = ({
  product,
  onSubmit,
  isLoading,
  loadingButton,
  isNew,
  brands
}) => {
  const { t } = useTranslation();

  const initFormikValues = {
    name: product?.name || '',
    price: product?.price || 0,
    description: product?.description || '',
    keywords: product?.keywords || [],
    sold: product?.sold || false,
    featured: product?.featured || false,
    visible: product?.visible || false,
    category: product?.category || {},
    address: product?.address || {}
  };
  const FormSchema = Yup.object().shape({
    name: Yup.string()
      .required(t('productFormNameError1'))
      .max(60, t('productFormNameError2')),
    price: Yup.number()
      .integer(t('productFormPriceError2'))
      .required(t('productFormPriceError3'))
      .min(0, t('productFormPriceError1'))
      .max(100000, t('productFormPriceError4')),
    description: Yup.string()
      .required(t('productFormDescriptionError'))
      .max(200, t('productFormDescriptionErrorLength')),
    keywords: Yup.array()
      .of(Yup.string())
      .min(1, t('productFormKeywordError'))
      .max(10, t('productFormKeywordError')),
    sold: Yup.boolean()
  });

  const store = useSelector((state) => ({
    user: state.auth
  }));

  const { imageFile, isFileLoading, onFileChange, removeImage } =
    useFileHandler({
      image: {},
      imageCollection: product?.imageCollection || []
    });

  const onSubmitForm = (form) => {
    if (imageFile.image.file || product.image) {
      if (form.address.country === 'Svizzera') {
        delete form.address.region;
        delete form.address.province;
        delete form.address.municipality;
      } else if (form.address.country === 'Italia') {
        delete form.address.npa;
      }
      if (form.category.type === 'Accessorio') {
        delete form.category.sub1;
        delete form.category.sub2;
        delete form.category.assisted;
        delete form.category.year;
        delete form.category.size;
        delete form.category.weight;
        delete form.category.wheels;
        delete form.category.brand;
      }
      onSubmit({
        ...form,
        userId: store.user.id,
        name_lower: form.name.toLowerCase(),
        dateAdded: isNew ? new Date().getTime() : product.dateAdded,
        image: imageFile?.image?.file || product.image,
        imageCollection: imageFile.imageCollection
      });
    } else {
      displayActionMessage('productFormImageError', 'error');
    }
  };

  return (
    <div>
      <Formik
        initialValues={initFormikValues}
        validateOnChange
        validationSchema={FormSchema}
        onSubmit={onSubmitForm}
      >
        {({ values, setValues }) => (
          <Form>
            <div className="product-form">
              <div className="product-form-file">
                <div className="product-form-field">
                  <span className="d-block padding-s">
                    * {t('productFormImage')}
                  </span>
                  {!isFileLoading && (
                    <label htmlFor="product-input-file">
                      <input
                        disabled={isLoading}
                        hidden
                        id="product-input-file"
                        onChange={(e) =>
                          onFileChange(e, { name: 'image', type: 'single' })
                        }
                        readOnly={isLoading}
                        type="file"
                      />
                      {t('productFormImageButton')}
                    </label>
                  )}
                </div>
                <div className="product-form-image-wrapper">
                  {(imageFile.image.url || product.image) && (
                    <ImageLoader
                      alt=""
                      className="product-form-image-preview"
                      src={imageFile.image.url || product.image}
                    />
                  )}
                </div>
              </div>
              <div className="product-form-inputs">
                <div className="product-form-field">
                  <Field
                    disabled={isLoading}
                    name="name"
                    type="text"
                    label={`* ${t('productFormName')}`}
                    style={{ textTransform: 'capitalize' }}
                    component={CustomInput}
                  />
                </div>
                &nbsp;
                <div className="product-form-field">
                  <CustomCategorySelect
                    brandsList={brands}
                    name="category"
                    defaultType={product.category.type}
                    defaultSub1={product.category.sub1}
                    defaultSub2={product.category.sub2}
                    defaultYear={product.category.year}
                    defaultAssisted={product.category.assisted}
                    defaultSize={product.category.size}
                    defaultWeight={product.category.weight}
                    defaultWheels={product.category.wheels}
                    defaultBrand={product.category.brand}
                  />
                </div>
                &nbsp;
                <div className="product-form-field">
                  <CustomAddressSelect
                    name="address"
                    defaultCountry={product.address.country}
                    defaultNpa={product.address.npa}
                    defaultMunicipality={product.address.municipality}
                    defaultProvince={product.address.province}
                    defaultRegion={product.address.region}
                  />
                </div>
                <div className="product-form-field">
                  <Field
                    disabled={isLoading}
                    name="description"
                    id="description"
                    rows={3}
                    label={`* ${t('productFormDescription')}`}
                    component={CustomTextarea}
                  />
                </div>
                <div className="product-form-field">
                  <Field
                    disabled={isLoading}
                    name="price"
                    id="price"
                    type="number"
                    label={`* ${t('productFormPrice')}`}
                    component={CustomInput}
                  />
                </div>
                <div className="product-form-field">
                  <CustomCreatableSelect
                    defaultValue={values.keywords.map((key) => ({
                      value: key,
                      label: key
                    }))}
                    name="keywords"
                    iid="keywords"
                    isMulti
                    disabled={isLoading}
                    label={`* ${t('productFormKeyword')}`}
                    placeholder={t('productFormKeywordPlaceholder')}
                  />
                </div>
                {!isNew && (
                  <div className="product-form-field">
                    <input
                      checked={values.sold}
                      id="sold"
                      onChange={(e) =>
                        setValues({ ...values, sold: e.target.checked })
                      }
                      type="checkbox"
                    />
                    <label htmlFor="sold">
                      <h5 className="d-flex-grow-1 margin-0">
                        &nbsp; {t('productFormSold')} &nbsp;
                      </h5>
                    </label>
                  </div>
                )}
                <div className="product-form-field">
                  <span className="d-block padding-s">
                    {t('productFormGallery')}
                  </span>
                  {!isFileLoading && (
                    <label htmlFor="product-input-file-collection">
                      <input
                        disabled={isLoading}
                        hidden
                        id="product-input-file-collection"
                        multiple
                        onChange={(e) =>
                          onFileChange(e, {
                            name: 'imageCollection',
                            type: 'multiple'
                          })
                        }
                        readOnly={isLoading}
                        type="file"
                      />
                      {t('productFormGalleryButton')}
                    </label>
                  )}
                </div>
                <div className="product-form-collection">
                  {imageFile.imageCollection.length >= 1 &&
                    imageFile.imageCollection.map((image) => (
                      <div
                        className="product-form-collection-image"
                        key={image.id}
                      >
                        <ImageLoader alt="" src={image.url} />
                        <button
                          className="product-form-delete-image"
                          onClick={() =>
                            removeImage({
                              id: image.id,
                              name: 'imageCollection'
                            })
                          }
                          type="button"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            </div>
            <div className="product-form-submit">
              <br />
              <br />
              <button
                className="button w-100-mobile"
                disabled={isLoading || loadingButton}
                type="submit"
              >
                {isLoading || loadingButton ? (
                  <LoadingOutlined />
                ) : (
                  <CheckOutlined />
                )}
                &nbsp;
                {isLoading || loadingButton
                  ? t('loadingMessage')
                  : t('productFormSaveButton')}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

ProductForm.propTypes = {
  product: PropType.shape({
    name: PropType.string,
    price: PropType.number,
    description: PropType.string,
    keywords: PropType.arrayOf(PropType.string),
    imageCollection: PropType.arrayOf(PropType.object),
    image: PropType.string,
    imageUrl: PropType.string,
    sold: PropType.bool,
    featured: PropType.bool,
    visible: PropType.bool,
    dateAdded: PropType.number,
    category: PropType.shape({
      type: PropType.string,
      sub1: PropType.string,
      sub2: PropType.string,
      assisted: PropType.string,
      year: PropType.number,
      weight: PropType.number,
      size: PropType.string,
      wheels: PropType.number,
      brand: PropType.string
    }),
    address: PropType.shape({
      country: PropType.string,
      province: PropType.string,
      region: PropType.string,
      municipality: PropType.string,
      npa: PropType.string
    })
  }).isRequired,
  onSubmit: PropType.func.isRequired,
  isLoading: PropType.bool.isRequired,
  loadingButton: PropType.bool.isRequired,
  isNew: PropType.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  brands: PropType.any.isRequired
};

export default ProductForm;
